import { FC, useContext, useEffect, useState } from 'react';

import { useLazyQuery, useMutation } from '@apollo/client';
import { LeftLogoIcon } from '@smartshell/ui-kit';
import cn from 'classnames';
import { useNavigate } from 'react-router-dom';

import { LoadableNotification, Version } from 'components';
import { usePageTitles } from 'hooks';
import { isSandbox } from 'sandbox';

import { AuthContext } from '../constants';
import { GET_ME, LOGIN, USER_CLUBS } from '../schema';
import {
  IGetMeResponse,
  ILoginResponse,
  IUserClubsRequest,
  IUserClubsResponse,
  PermissionKey,
} from '../types';
import { Clubs } from './Clubs';
import { Credentials } from './Credentials';
import ErrorWorkShift from './ErrorWorkShift';
import { ICredentials, TActiveWindow } from './interfaces';
import s from './Login.module.scss';
import { LoginLanguageSwitcher } from './LoginLanguageSwitcher';
import { OpenWorkShift } from './OpenWorkShift';

export const Login: FC = () => {
  usePageTitles();
  const [activeWindow, setActiveWindow] =
    useState<TActiveWindow>('CREDENTIALS');
  const [credentials, setCredentials] = useState<ICredentials | null>(null);
  const [selectedClubId, setSelectedClubId] = useState<number | null>(null);

  const {
    actions: { setTokens, clearError, setError, logout, setMe, setPermissions },
  } = useContext(AuthContext);

  const navigate = useNavigate();

  const [getUserClubs, { data: clubs }] = useLazyQuery<
    IUserClubsResponse,
    IUserClubsRequest
  >(USER_CLUBS, {
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'none',
    onCompleted: (data) => {
      if (!data.userClubs || !data.userClubs?.length) {
        return setError({
          title: 'errors.login.noClubs.title',
          message: 'errors.login.noClubs.description',
        });
      }

      if (data.userClubs.length === 1 && !isSandbox) {
        setSelectedClubId(data.userClubs[0].id);

        return login({
          variables: {
            input: { ...credentials, company_id: data.userClubs[0].id },
          },
        });
      }

      setActiveWindow('CLUBS');
    },
    onError: (error) => {
      if (error.message.includes('incorrect login or password')) {
        return setError({
          title: 'errors.login.credentials.title',
          message: 'errors.login.credentials.description',
        });
      }

      return setError({
        title: 'errors.unknown.title',
        message: 'errors.login.unknown.description',
      });
    },
  });

  const [login, { data: loginResponse }] = useMutation<ILoginResponse>(LOGIN, {
    onCompleted: (data) => {
      clearError();

      setTokens(data.login);
    },
  });

  const selectedClub = clubs?.userClubs.find(
    (club) => club.id === selectedClubId
  );

  const [loadMe] = useLazyQuery<IGetMeResponse>(GET_ME, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (response) => {
      if (!response?.me) return;
      if (!selectedClubId) return;

      setMe(response?.me);

      const { permissions } = response?.me;

      const permissionsSet = new Set<PermissionKey>();

      permissions.forEach(({ alias }) => permissionsSet.add(alias));

      setPermissions(permissionsSet);

      const isOpen = permissionsSet.has('work_shift_open');
      const isCanInto = permissionsSet.has('can_login_into_work_shift');

      const redirectToDashboard = (): void => navigate('/', { replace: true });

      switch (selectedClub?.workShiftStatus) {
        case 'MY':
          return redirectToDashboard();

        case 'NONE': {
          if (isOpen) return setActiveWindow('WORK_SHIFT');
          else return redirectToDashboard();
        }

        case 'OPEN': {
          if (isCanInto) return redirectToDashboard();

          return setActiveWindow('WORK_SHIFT_ERROR');
        }
      }
    },
  });

  useEffect(() => {
    if (loginResponse?.login.access_token) {
      loadMe({
        context: {
          headers: {
            Authorization: `Bearer ${loginResponse?.login.access_token}`,
          },
        },
      });
    }
  }, [loginResponse?.login.access_token]);

  return (
    <div className={cn(s.container, activeWindow === 'CLUBS' && s.border)}>
      <LeftLogoIcon className={s.icon} />

      <div className={s['form-section']}>
        {activeWindow === 'CREDENTIALS' && <LoadableNotification />}
        <div className={s['form-container']}>
          {activeWindow === 'CREDENTIALS' && (
            <Credentials
              onSubmit={(formValues) => {
                clearError();
                setCredentials(formValues);

                return getUserClubs({ variables: { input: formValues } }).catch(
                  console.error
                );
              }}
            />
          )}
          {activeWindow === 'CLUBS' && (
            <Clubs
              clubs={clubs?.userClubs ?? []}
              onClubSelect={(clubId) => {
                setSelectedClubId(clubId);

                return login({
                  variables: {
                    input: { ...credentials, company_id: clubId },
                  },
                });
              }}
            />
          )}
          {activeWindow === 'WORK_SHIFT' && (
            <OpenWorkShift
              returnToClubSelect={() => {
                logout();

                if (credentials) {
                  setActiveWindow('CLUBS');
                } else {
                  setActiveWindow('CREDENTIALS');
                }
              }}
              token={loginResponse?.login.access_token}
              selectedClub={selectedClub}
            />
          )}
          {activeWindow === 'WORK_SHIFT_ERROR' && (
            <ErrorWorkShift
              returnToClubSelect={() => {
                logout();

                if (credentials) {
                  setActiveWindow('CLUBS');
                } else {
                  setActiveWindow('CREDENTIALS');
                }
              }}
            />
          )}
        </div>
      </div>
      <div className={s['footer-controls']}>
        <LoginLanguageSwitcher />
        <Version showOnTop />
      </div>
    </div>
  );
};
